'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import {
  defaultLanguage,
  getLanguageFromPath,
  getLanguageFromValue,
  uiText,
  withLanguagePrefix,
  type SiteLanguage,
} from '@/data/languages';

interface NewsArticle {
  id: number | string;
  slug: string;
  title: string;
  excerpt: string;
  href: string;
  image: string;
  alt: string;
  tag: string;
  published_at?: string;
  date?: string;
  featured: boolean;
}

const tagColors: Record<string, { bg: string; text: string; border: string }> = {
  Award: { bg: 'rgba(245,158,11,0.15)', text: '#F59E0B', border: 'rgba(245,158,11,0.3)' },
  Services: { bg: 'rgba(27,107,138,0.15)', text: '#2A8FAB', border: 'rgba(27,107,138,0.3)' },
  Promotion: { bg: 'rgba(16,185,129,0.15)', text: '#10B981', border: 'rgba(16,185,129,0.3)' },
  Digital: { bg: 'rgba(99,102,241,0.15)', text: '#818CF8', border: 'rgba(99,102,241,0.3)' },
  Certification: { bg: 'rgba(239,68,68,0.15)', text: '#F87171', border: 'rgba(239,68,68,0.3)' },
  Expansion: { bg: 'rgba(16,185,129,0.15)', text: '#059669', border: 'rgba(16,185,129,0.3)' },
};

export default function NewsPromotionsPage() {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [activeTag, setActiveTag] = useState('All');
  const [tags, setTags] = useState<string[]>(['All']);
  const [language, setLanguage] = useState<SiteLanguage>(defaultLanguage);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);
  const t = uiText[language];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const selectedLanguage = getLanguageFromPath(window.location.pathname)
      ?? getLanguageFromValue(params.get('lang') || localStorage.getItem('wto_lang'));
    setLanguage(selectedLanguage);
    localStorage.setItem('wto_lang', selectedLanguage);

    fetch(`/news-api.php?lang=${selectedLanguage}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Unable to load news.')))
      .then((payload) => {
        const loaded: NewsArticle[] = payload.articles ?? [];
        setArticles(loaded);
        setTags([uiText[selectedLanguage].all, ...Array.from(new Set(loaded.map((a) => a.tag)))]);
        setActiveTag(uiText[selectedLanguage].all);
      })
      .catch(() => {
        setArticles([]);
        setTags([uiText[selectedLanguage].all]);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeTag === t.all ? articles : articles.filter((a) => a.tag === activeTag);
  const featured = filtered.find((a) => a.featured) || filtered[0];
  const rest = filtered.filter((a) => a.id !== featured?.id);

  useEffect(() => {
    const els = sectionRef.current?.querySelectorAll('.reveal');
    if (!els) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [filtered]);

  const withLanguage = (href: string) => {
    const [pathWithQuery, hash = ''] = href.split('#');
    const [path, query = ''] = pathWithQuery.split('?');
    const nextPath = withLanguagePrefix(path || '/', language);
    return `${nextPath}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
  };

  return (
    <>
      <Header />
      <main className="min-h-screen" style={{ background: '#F4F6F9' }}>
        <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-24" style={{ background: 'linear-gradient(135deg, #0A1535 0%, #0F2340 50%, #1A3558 100%)' }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="flex items-center gap-2 mb-5">
              <Link href="/home-page" className="text-sm font-body transition-colors" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {t.home}
              </Link>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(255,255,255,0.3)' }}>
                <path d="M9 18l6-6-6-6" />
              </svg>
              <span className="text-sm font-body" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.newsTitle}</span>
            </div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-body mb-4" style={{ color: '#7EC8DC' }}>
              <span className="w-6 h-px" style={{ background: '#7EC8DC' }} />
              {t.latestUpdates}
            </span>
            <h1 className="font-display text-5xl lg:text-6xl xl:text-7xl leading-tight mb-6" style={{ color: '#FFFFFF' }}>
              {t.newsTitle}
            </h1>
            <p className="text-lg font-body max-w-2xl leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
              {t.newsSubtitle}
            </p>
          </div>
        </section>

        <div className="sticky top-16 lg:top-20 z-30 border-b" style={{ background: 'rgba(244,246,249,0.97)', backdropFilter: 'blur(12px)', borderColor: 'rgba(15,35,64,0.1)' }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
              {tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className="shrink-0 px-4 py-1.5 rounded-full text-sm font-medium font-body transition-all duration-200"
                  style={activeTag === tag ? { background: '#0F2340', color: '#FFFFFF', border: '1px solid #0F2340' } : { background: 'transparent', color: '#3A5070', border: '1px solid rgba(15,35,64,0.2)' }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 lg:py-20" ref={sectionRef}>
          {loading || filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg font-body" style={{ color: '#6A85A0' }}>{loading ? 'Loading...' : t.noArticles}</p>
            </div>
          ) : (
            <>
              {featured && (
                <a href={withLanguage(featured.href)} className="reveal group block rounded-3xl overflow-hidden mb-10 transition-all duration-300 hover:shadow-2xl" style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)', boxShadow: '0 4px 24px rgba(15,35,64,0.08)' }}>
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="relative overflow-hidden" style={{ minHeight: '320px' }}>
                      <AppImage src={featured.image} alt={featured.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-700" sizes="(max-width: 1024px) 100vw, 50vw" />
                      <div className="absolute top-5 left-5">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold font-body" style={tagColors[featured.tag] ? { background: tagColors[featured.tag].bg, color: tagColors[featured.tag].text, border: `1px solid ${tagColors[featured.tag].border}` } : { background: 'rgba(27,107,138,0.85)', color: '#FFFFFF', border: '1px solid rgba(27,107,138,0.5)' }}>
                          {featured.tag}
                        </span>
                      </div>
                      <div className="absolute top-5 right-5 px-2.5 py-1 rounded-full text-xs font-medium font-body backdrop-blur-sm" style={{ background: 'rgba(0,0,0,0.4)', color: '#FFFFFF' }}>
                        {t.featured}
                      </div>
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <p className="text-xs font-body uppercase tracking-widest mb-3" style={{ color: '#6A85A0' }}>{featured.date || featured.published_at}</p>
                      <h2 className="font-display text-2xl lg:text-3xl leading-snug mb-4 transition-colors duration-300 group-hover:text-[#1B6B8A]" style={{ color: '#0F2340' }}>{featured.title}</h2>
                      <p className="text-base font-body leading-relaxed mb-6" style={{ color: '#3A5070' }}>{featured.excerpt}</p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold font-body group-hover:gap-3 transition-all duration-200" style={{ color: '#1B6B8A' }}>{t.readFullStory}</span>
                    </div>
                  </div>
                </a>
              )}

              {rest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((article, i) => (
                    <a key={article.id} href={withLanguage(article.href)} className="reveal group rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1" style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)', boxShadow: '0 2px 16px rgba(15,35,64,0.06)', transitionDelay: `${i * 80}ms` }}>
                      <div className="relative overflow-hidden" style={{ height: '220px' }}>
                        <AppImage src={article.image} alt={article.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-700" sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" />
                        <div className="absolute top-4 left-4">
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold font-body backdrop-blur-sm" style={tagColors[article.tag] ? { background: tagColors[article.tag].bg, color: tagColors[article.tag].text, border: `1px solid ${tagColors[article.tag].border}` } : { background: 'rgba(27,107,138,0.85)', color: '#FFFFFF', border: '1px solid rgba(27,107,138,0.5)' }}>
                            {article.tag}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <p className="text-xs font-body uppercase tracking-wide mb-2" style={{ color: '#6A85A0' }}>{article.date || article.published_at}</p>
                        <h3 className="font-display text-lg leading-snug mb-3 transition-colors duration-300 group-hover:text-[#1B6B8A]" style={{ color: '#0F2340' }}>{article.title}</h3>
                        <p className="text-sm font-body leading-relaxed line-clamp-3 mb-4" style={{ color: '#3A5070' }}>{article.excerpt}</p>
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold font-body group-hover:gap-2.5 transition-all duration-200" style={{ color: '#1B6B8A' }}>{t.readMore}</span>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
